/**
 * ColumnsTab — column list for `TableEditor`. Read-only when `editContext` is
 * null (Slice 0.4 scaffolding). When `editContext` is supplied (Phase A1+) the
 * per-row Edit affordance opens the `ColumnFormPopover` and commits an
 * `alter-column` op via `commitAlterColumn`, and Add opens the
 * `ColumnAddPopover`. Drop is NOT rendered here at all: it was never wired in
 * either variant (no code path ever enabled it), so a permanently dead × would
 * be pure noise — “Edit as SQL” covers dropping a column.
 *
 * `compact` is a pure density/visibility knob for the compact Visual DDL
 * popover: tighter cells + smaller buttons, the Default column plus the
 * dependency annotation chips drop out (Name / Type / Nullable / Actions stay),
 * and the section label is suppressed because the popover's tab strip already
 * says “Columns (n)” directly above it. No row is filtered and no affordance
 * changes behaviour.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice 0.4 (read-only) + Slice A1 (alter).
 * Redesign: .superpowers/sdd/2026-07-28-compact-visual-ddl-editor/
 */
import { Pencil, Plus } from 'lucide-react';

import { cn } from '../../../../../lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../../components/ui/table';
import type { DdlStructure, IdentitySpec } from '../../../../sql-resolver/types/ddlEdit';
import { sqlEngineToDatabaseEngineId } from '../../../../../core/engines';
import { JsonColumnToggleCell } from '../../../table-designer/components/JsonColumnToggleCell';
import { resolveDefaultSchemaForEngine } from '../../../common/utils/defaultSchemaForEngine';
import type { DependencyAnnotations } from '../../dependencies/types';
import {
  getAlterColumnUnsupportedTooltip,
  isAlterColumnSupported,
} from '../../edit/edits/alterColumn';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { ColumnAddPopover } from '../common/ColumnAddPopover';
import { ColumnFormPopover } from '../common/ColumnFormPopover';
import { DisabledAffordance } from '../common/DisabledAffordance';
import { ColumnAnnotationChips } from './ColumnAnnotationChips';
import { columnRowsFromStructure, type ColumnRow } from './columnRows';

const ADD_COLUMN_TOOLTIP = 'Connect an edit context to add columns';
const EDIT_COLUMN_TOOLTIP_DEFAULT =
  'Column editing isn’t available in this read-only view — use “Edit as SQL” to change the column.';
const EMPTY_COLUMNS_HINT =
  'This statement doesn’t define any columns yet — add one with + Column.';
const IMPLIED_NULL_HINT = 'Not stated in the statement — the column allows NULL by default.';

/** Per-row action chip copy. `declared` is the default state of a CREATE TABLE
 *  column, so it gets NO chip — badging every row "DECLARED" is noise. */
const ACTION_LABELS: Record<Exclude<ColumnRow['action'], 'declared'>, string> = {
  added: 'added',
  dropped: 'dropped',
  renamed: 'renamed',
};

function describeIdentity(spec: IdentitySpec): string {
  const parts: string[] = [];
  if (spec.mode === 'always') parts.push('GENERATED ALWAYS');
  else if (spec.mode === 'by-default') parts.push('GENERATED BY DEFAULT');
  else if (spec.mode === 'by-default-on-null') parts.push('GENERATED BY DEFAULT ON NULL');
  if (spec.start !== undefined) parts.push(`start ${spec.start}`);
  if (spec.increment !== undefined) parts.push(`increment ${spec.increment}`);
  if (spec.cycle === true) parts.push('CYCLE');
  else if (spec.cycle === false) parts.push('NO CYCLE');
  return parts.length === 0 ? 'Identity column' : parts.join(', ');
}

export interface ColumnsTabProps {
  readonly structure: DdlStructure | null;
  /** Required `data-testid` prefix for unit + E2E specs. */
  readonly testId?: string;
  /** Optional edit context — when supplied, per-row Edit becomes a popover
   *  bound to `commitAlterColumn`. When null/undefined, the tab stays
   *  read-only (Slice 0.4 scaffolding). */
  readonly editContext?: VqeDdlEditContext | null;
  /** Optional dependency-annotation surface (Slice H2). When supplied, per-row
   *  callouts render: incoming-FK count, dependent-view count, sequence
   *  default. The annotations come from the introspection-fallback IPC via
   *  `useDependencyAnnotations`; columns without an entry render no chips. */
  readonly dependencyAnnotations?: DependencyAnnotations | null;
  /** Compact density (compact Visual DDL popover): tighter cells / smaller
   *  buttons, and the low-value Default + annotation-chip columns drop out so
   *  Name / Type / Nullable / Actions fit the popover width. */
  readonly compact?: boolean;
}

function tableArm(
  structure: DdlStructure | null,
): { schema?: string; name: string } | null {
  if (!structure) return null;
  if (structure.kind === 'create-table' || structure.kind === 'alter-table') {
    return { schema: structure.table.schema, name: structure.table.name };
  }
  return null;
}

export function ColumnsTab({
  structure,
  testId = 'ddl-columns-tab',
  editContext,
  dependencyAnnotations,
  compact = false,
}: ColumnsTabProps) {
  const rows = columnRowsFromStructure(structure);
  const headClass = compact ? 'h-8 px-2 py-1 text-xs' : undefined;
  const cellClass = compact ? 'px-2 py-1' : undefined;
  const iconButtonClass = compact
    ? 'h-6 w-6 text-muted-foreground'
    : 'h-7 w-7 text-muted-foreground';
  const editEnabled =
    editContext != null && isAlterColumnSupported(editContext.service.engineId);
  const editTooltip =
    editContext != null && !editEnabled
      ? getAlterColumnUnsupportedTooltip(editContext.service.engineId)
      : EDIT_COLUMN_TOOLTIP_DEFAULT;

  // Phase 7 — "Treat as JSON column" toggle context. Only available when the
  // editor is bound to a live connection/database (Designer host); parsed-only
  // editors omit it and the column keeps its read-only rendering.
  const arm = tableArm(structure);
  const jsonCtx =
    editContext?.connectionId && editContext?.database && arm
      ? {
          connectionId: editContext.connectionId,
          database: editContext.database,
          engine: sqlEngineToDatabaseEngineId(editContext.service.engineId),
          table: arm.name,
        }
      : null;
  // The schema written into the MetaInfo JSON declaration is exact-matched
  // against real schema metadata by the grid/filter-chip categorization
  // path, so an unqualified-DDL default must be the engine's *effective*
  // schema (MySQL: database name; Oracle: connected user) — not a blanket
  // `dbo`, which would never match the real MySQL/Oracle schema and silently
  // drop the JSON UI for those engines.
  const jsonSchema = jsonCtx
    ? (arm?.schema ??
      resolveDefaultSchemaForEngine(
        jsonCtx.engine,
        jsonCtx.database,
        jsonCtx.connectionId,
      ))
    : undefined;

  return (
    <div
      className={cn('flex flex-col', compact ? 'gap-2' : 'gap-3')}
      data-testid={testId}
    >
      {/* Compact drops the section label: the popover's tab strip directly
          above already reads "Columns (n)", so repeating it here was a
          duplicate header. The [+ Column] action stays right-aligned. */}
      <div className={cn('flex items-center', compact ? 'justify-end' : 'justify-between')}>
        {compact ? null : (
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Columns ({rows.length})
          </div>
        )}
        {editContext ? (
          <ColumnAddPopover
            editContext={editContext}
            testId={`${testId}-add-column`}
          />
        ) : (
          <DisabledAffordance
            testId={`${testId}-add-column`}
            tooltip={ADD_COLUMN_TOOLTIP}
            variant="ghost"
            className={compact ? 'h-6 gap-1 px-1.5 text-xs text-muted-foreground' : undefined}
          >
            <Plus className="h-3.5 w-3.5" />
            Column
          </DisabledAffordance>
        )}
      </div>

      {rows.length === 0 ? (
        <div
          className={cn(
            'rounded-md border border-dashed border-border text-center text-xs text-muted-foreground',
            compact ? 'px-2 py-3' : 'px-3 py-6',
          )}
          data-testid={`${testId}-empty`}
        >
          {EMPTY_COLUMNS_HINT}
        </div>
      ) : (
        <Table data-testid={`${testId}-table`}>
          <TableHeader>
            <TableRow>
              <TableHead className={cn('w-[30%]', headClass)}>Name</TableHead>
              <TableHead className={cn('w-[25%]', headClass)}>Type</TableHead>
              {/* 15% + nowrap keeps the header on one line. */}
              <TableHead className={cn('w-[15%] whitespace-nowrap text-center', headClass)}>
                Nullable
              </TableHead>
              {compact ? null : <TableHead className="w-[15%]">Default</TableHead>}
              <TableHead className={cn('w-[15%] text-right', headClass)}>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.key}
                data-testid={`${testId}-row-${row.name}`}
                data-action={row.action}
              >
                <TableCell className={cn('font-mono text-xs', cellClass)}>
                  {row.previousName ? (
                    <span>
                      <span className="text-muted-foreground line-through">{row.previousName}</span>
                      <span className="mx-1 text-muted-foreground">→</span>
                      <span>{row.name}</span>
                    </span>
                  ) : (
                    row.name
                  )}
                  {row.action === 'declared' ? null : (
                    <span
                      className="ml-2 rounded-sm bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground"
                      data-testid={`${testId}-action-chip-${row.name}`}
                    >
                      {ACTION_LABELS[row.action]}
                    </span>
                  )}
                  {row.identity ? (
                    <span
                      className="ml-2 rounded-sm bg-amber-500/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-400"
                      data-testid={`${testId}-identity-badge-${row.name}`}
                      title={describeIdentity(row.identity)}
                    >
                      IDENTITY
                    </span>
                  ) : null}
                  {compact ? null : (
                    <ColumnAnnotationChips
                      columnName={row.name}
                      defaultText={row.defaultText}
                      annotations={dependencyAnnotations ?? null}
                      testId={testId}
                    />
                  )}
                </TableCell>
                <TableCell className={cn('font-mono text-xs', cellClass)}>
                  {row.typeRef ?? <span className="text-muted-foreground">—</span>}
                  {jsonCtx && jsonSchema !== undefined ? (
                    <JsonColumnToggleCell
                      connectionId={jsonCtx.connectionId}
                      database={jsonCtx.database}
                      schema={jsonSchema}
                      table={jsonCtx.table}
                      column={row.name}
                      dataType={row.typeRef}
                      engine={jsonCtx.engine}
                      dropped={row.action === 'dropped'}
                      testId={`${testId}-json-toggle-${row.name}`}
                    />
                  ) : null}
                </TableCell>
                {/* The column header is "Nullable", so the cell answers it:
                    Yes / No (it used to print the SQL tokens NULL / NOT NULL).
                    An unstated column is nullable by default — a muted "Yes"
                    with the reason as tooltip, never a bare "—". A row with no
                    type at all (drop / rename clause) genuinely carries no
                    nullability, and keeps the em dash. */}
                <TableCell
                  className={cn('whitespace-nowrap text-center text-xs', cellClass)}
                >
                  {row.nullable === false ? (
                    'No'
                  ) : row.nullable === true ? (
                    'Yes'
                  ) : row.typeRef === null ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <span className="text-muted-foreground" title={IMPLIED_NULL_HINT}>
                      Yes
                    </span>
                  )}
                </TableCell>
                {compact ? null : (
                  <TableCell className="font-mono text-xs">
                    {row.defaultText ?? <span className="text-muted-foreground">—</span>}
                  </TableCell>
                )}
                <TableCell className={cn('text-right', cellClass)}>
                  <div className="flex items-center justify-end gap-1">
                    {editEnabled && editContext && row.action !== 'dropped' ? (
                      <ColumnFormPopover
                        editContext={editContext}
                        columnName={row.name}
                        // The row IS the occurrence the user clicked. A source
                        // may declare one byte-name twice with different
                        // delimiters — two different columns on a folding
                        // engine — and the name alone cannot say which row
                        // this is, so the row's own spelling travels with the
                        // edit instead of the applicator refusing it.
                        columnNameOrigin={row.nameOrigin}
                        currentType={row.typeRef === '—' ? null : row.typeRef}
                        currentNullable={row.nullable}
                        currentDefault={row.defaultText}
                        testId={`${testId}-edit-${row.name}`}
                      />
                    ) : (
                      <DisabledAffordance
                        testId={`${testId}-edit-${row.name}`}
                        tooltip={editTooltip}
                        variant="ghost"
                        size="icon"
                        className={iconButtonClass}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </DisabledAffordance>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

